import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, fetchPublicBookingConfig } from "@/lib/queryClient";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { ContentCut, CheckCircle } from "@mui/icons-material";
import ThemeToggle from "@/components/ThemeToggle";
import { useTranslation } from "react-i18next";
import { tryGetPublicSlug } from "@/lib/bookingApi";
import { validateEmailRequired } from "@/lib/formValidation";
import type { Business } from "@shared/schema";

export default function AccountLogin() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailFieldError, setEmailFieldError] = useState<string | undefined>();
  const [sent, setSent] = useState(false);
  const publicSlug = tryGetPublicSlug();
  const { data: business } = useQuery<Business>({
    queryKey: ["public-config", publicSlug],
    queryFn: async () => {
      const br = await fetchPublicBookingConfig(publicSlug!);
      return {
        id: br.companyId,
        name: br.name,
        slug: br.slug,
        timezone: br.timezone,
        currency: br.currency,
        category: "beauty",
        address: null,
        phone: null,
        email: null,
        website: null,
        description: null,
        logoUrl: null,
        coverImageUrl: null,
        accentColor: "#7c3aed",
        minLeadTimeMins: br.minLeadTimeMins,
        maxBookingDays: br.maxBookingHorizonDays,
        slotIncrementMins: br.slotIncrementMins,
        cancellationPolicyText: br.cancellationPolicyText ?? null,
        depositEnabled: br.defaultDepositEnabled,
        defaultDepositType: br.defaultDepositType,
        defaultDepositValue: br.defaultDepositValue,
        bookingPageConfig: null,
        bookingPageDraftConfig: null,
        brandingUpdatedAt: null,
      } as Business;
    },
    enabled: !!publicSlug,
  });

  const sendLinkMutation = useMutation({
    mutationFn: (em: string) => apiRequest("POST", "/api/account/request-link", { email: em }),
    onSuccess: () => setSent(true),
    onError: () => {
      setSent(true);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateEmailRequired(email);
    setEmailFieldError(err ?? undefined);
    if (err) return;
    sendLinkMutation.mutate(email.trim());
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar sx={{ maxWidth: 480, mx: "auto", width: "100%" }}>
          <ContentCut color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            {business?.name || t("common.poweredByDefault")}
          </Typography>
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ maxWidth: 480, mx: "auto", px: 2, py: 8 }}>
        <Card variant="outlined">
          <CardHeader
            title={t("accountLogin.cardTitle")}
            subheader={sent ? t("accountLogin.cardSubheaderSent") : t("accountLogin.cardSubheaderPrompt")}
            sx={{ textAlign: "center" }}
          />
          <CardContent>
            {sent ? (
              <Box sx={{ textAlign: "center" }}>
                <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t("accountLogin.checkEmailBody", { email })}
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 3 }}
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                    setEmailFieldError(undefined);
                  }}
                >
                  {t("accountLogin.tryDifferentEmail")}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  id="email"
                  label={t("accountLogin.emailLabel")}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailFieldError(undefined);
                  }}
                  error={Boolean(emailFieldError)}
                  helperText={emailFieldError}
                  required
                  fullWidth
                  data-testid="input-account-email"
                />
                <Button type="submit" variant="contained" fullWidth disabled={sendLinkMutation.isPending || !email.trim()} data-testid="button-send-magic-link">
                  {sendLinkMutation.isPending ? t("common.sending") : t("accountLogin.sendSignInLink")}
                </Button>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {t("accountLogin.noPasswordHint")}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
